import React, { createContext, useContext, useMemo } from "react";
import { clinicsMap, DEFAULT_CLINIC_ID, getClinicById } from "../data/mock";

// Create the context
const ClinicContext = createContext(null);

/**
 * ClinicProvider - Provides clinic information based on URL query parameter
 * Usage: Add ?clinic=<clinic-id> to URL (e.g., ?clinic=al-majd-dental)
 * Falls back to default clinic (sample-clinic) if not specified or invalid
 */
export const ClinicProvider = ({ children }) => {
  const clinicInfo = useMemo(() => {
    // Get clinic ID from URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const clinicId = urlParams.get("clinic");

    // Get clinic info (with fallback to default)
    return getClinicById(clinicId);
  }, []);

  const value = useMemo(
    () => ({
      clinicInfo,
      clinicsMap,
      defaultClinicId: DEFAULT_CLINIC_ID,
      currentClinicId: clinicInfo.id,
    }),
    [clinicInfo],
  );

  return (
    <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>
  );
};

/**
 * useClinic - Hook to access clinic information
 * @returns {{ clinicInfo: object, clinicsMap: object, defaultClinicId: string, currentClinicId: string }}
 */
export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error("useClinic must be used within a ClinicProvider");
  }
  return context;
};

export default ClinicContext;
